import { useState, useEffect, useMemo } from "react";
import DialogContent from "@mui/material/DialogContent";
import { useDispatch, useSelector } from "react-redux";
import { IRootState } from "src/redux/reducers/CombinedReducers";
import { DialogActions, useTheme } from "@mui/material";
import { SelectField } from "../../FormikComponents/SelectField";
import { ModalTitle, ModalMessage, ModalButton, ModalErrorText, ModalWarningText } from "../DialogModalComponents";
import { EventModalTypes } from "../DialogModalWrapper";
import { IDisplayPairing, IPairingPosition } from "@vmosolutions/oc-interfaces";
import { ICrewEvent, isIDisplayPairing, ICrewMember } from "src/redux/reducers/CrewScheduleReducer";
import { splitSelectedPairing } from "src/redux/actions/CrewSchedule/pairings";
import { openWindow } from "src/redux/actions/Mosaic.Actions";
import { MosaicDirection } from "src/utils/MosaicTree";
import { DialogModalStyles } from "../DialogModalWrapper";
import { IPosition } from "@vmosolutions/oc-interfaces";

interface ISplitPairing {
  isOpen: boolean;
  handleClose: (value: boolean) => void;
  setActiveEventModal: (EventModalTypes: EventModalTypes) => void;
}

interface ISplitPayload {
  pairingId: string;
  crewIds: string[];
}

interface ICrewOption {
  value: string;
  label: string;
  getDisplayOrder: () => number;
}

/**
 *  Modal to select pairing crew and split out to new pairing.
 *  ISplitPairing
 */
export const SplitPairingModal = (props: ISplitPairing): JSX.Element => {
  const { isOpen, handleClose, setActiveEventModal } = props;
  const dispatch = useDispatch();
  const theme = useTheme();
  const selectedCrewEvents: ICrewEvent[] = useSelector((state: IRootState) => state.crewSchedule.selectedCrewEvents);
  const allCrewMembers: ICrewMember[] = useSelector((state: IRootState) => state.crewSchedule.crewMembers);
  const allPositionCollection: IPosition[] = useSelector(
    (state: IRootState) => state.structures.customerStructure.positions
  ).sort((a, b) => (b.displayOrder > a.displayOrder ? -1 : 1));
  const [crewList, setCrewList] = useState<ICrewOption[]>([]);
  const [splitCrew, setSplitCrew] = useState<ICrewOption[]>([]);

  const [errorText, setErrorText] = useState<string>(null);

  // Return selected pairing.
  const selectedPairing: IDisplayPairing | null = useMemo(() => {
    const events: ICrewEvent[] = selectedCrewEvents.filter(
      (evnt: ICrewEvent) => isIDisplayPairing(evnt.event) && !evnt.isPlanned
    );
    return events.length > 0 ? (events[0].event as IDisplayPairing) : null;
  }, [selectedCrewEvents]);

  // Create crew options from available crew on the selected pairing and their "assigned" positions.
  useEffect(() => {
    if (!selectedPairing) return;

    const list: ICrewOption[] = [];
    selectedPairing.positions.forEach((position: IPairingPosition) => {
      const crewMember = allCrewMembers.find((crew: ICrewMember) => crew._id === position.crew_id);
      if (crewMember?._id) {
        const crewOption: ICrewOption = {
          label: `${crewMember.lastName}, ${crewMember.firstName} (${position.position})`,
          value: crewMember._id,
          getDisplayOrder() {
            return allPositionCollection.find((x) => x.title === position.position).displayOrder;
          },
        };
        list.push(crewOption);
      }
    });

    if (list.length === 1) {
      setErrorText("Cannot split a pairing containing only one crew member");
    } else {
      setErrorText(null);
    }

    setCrewList(
      list.sort((a, b) => {
        const displayOrderDifference = a.getDisplayOrder() - b.getDisplayOrder();
        // Sort by position
        if (displayOrderDifference !== 0) return displayOrderDifference;
        // Sort by name if same position
        else return a.label.toLowerCase().localeCompare(b.label.toLowerCase());
      })
    );
  }, [allCrewMembers, selectedPairing]);

  const splitPayload: ISplitPayload | null = useMemo(() => {
    if (!selectedPairing) return null;

    const crewIds = splitCrew.map((option: ICrewOption) => option.value);
    return {
      pairingId: selectedPairing._id,
      crewIds: crewIds,
    };
  }, [selectedPairing, splitCrew]);

  const leavesNoPositions: boolean =
    !!splitPayload &&
    selectedPairing.positions.every((position) => splitPayload.crewIds.includes(String(position.crew_id)));

  const cantSplit: boolean =
    !splitPayload ||
    !splitPayload?.pairingId ||
    !splitPayload?.crewIds ||
    splitPayload?.crewIds?.length < 1 ||
    crewList?.length === 1 ||
    leavesNoPositions;

  //////////////
  // HANDLERS //
  //////////////
  const handleConfirm = async (): Promise<void> => {
    if (cantSplit) {
      return;
    }

    try {
      const newSplitPairing = await splitSelectedPairing(splitPayload, dispatch);
      if (newSplitPairing?._id) {
        // Open split pairing in a new editor.
        dispatch(
          openWindow(
            "Edit Pairing",
            "PairingEditorWindow",
            [],
            { pairingId: newSplitPairing._id },
            null,
            true,
            MosaicDirection.Right,
            940
          )
        );
        // Reset and close modal.
        handleClose(false);
        setActiveEventModal(EventModalTypes.None);
      }
    } catch (error) {
      // TODO: Determine error text/typing properly.
      // eslint-disable-next-line @typescript-eslint/no-base-to-string
      setErrorText(error.toString());
    }
  };

  const handleCancel = (): void => {
    //cancel modal
    handleClose(false);
    setActiveEventModal(EventModalTypes.None);
  };

  if (isOpen && selectedPairing) {
    return (
      <DialogContent sx={DialogModalStyles(theme)}>
        <div className="modal_content" style={{ display: "flex", maxWidth: 500 }}>
          <ModalTitle title="Split Pairing" />
          <ModalMessage message="Select crew members to split from this pairing into a new one." />

          <SelectField
            className="modal_selection"
            label="Crew Members"
            isMulti={true}
            name="splitCrew"
            options={crewList}
            onChange={setSplitCrew}
            placeholder="Select Crew..."
            menuPosition="fixed"
            maxMenuHeight={400}
            openMenuOnFocus={false}
            value={splitCrew}
          />

          {!splitPayload?.crewIds && (
            <ModalWarningText warning="At least one crew member must be selected to split the pairing"></ModalWarningText>
          )}

          {leavesNoPositions && (
            <ModalWarningText warning="At least one position must remain on the original pairing"></ModalWarningText>
          )}

          {errorText && <ModalErrorText error={errorText}></ModalErrorText>}

          <DialogActions>
            <ModalButton label="Cancel" handler={handleCancel}></ModalButton>
            <ModalButton label="Split Pairing" disable={cantSplit} handler={handleConfirm}></ModalButton>
          </DialogActions>
        </div>
      </DialogContent>
    );
  }

  return null;
};
